import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    Folder,
    MonetizationOn,
    People,
    Inventory,
    Add,
    Description,
    PersonAdd,
    History
} from '@mui/icons-material';
import { projectsApi } from '../services/projectsApi';
import { customersApi } from '../services/customersApi';
import { estimatesApi } from '../services/estimatesApi';
import { materialsApi } from '../services/materialsApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import './Dashboard.css';

const Dashboard: React.FC = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<any[]>([]);
    const [stats, setStats] = useState({
        projects: 0,
        estimates: 0,
        customers: 0,
        materials: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            // Fetch all data in parallel
            const [projectsRes, customersRes, estimatesRes, materialsRes] = await Promise.all([
                projectsApi.getAll().catch(() => ({ data: [] })),
                customersApi.getAll().catch(() => ({ data: [] })),
                estimatesApi.getAll().catch(() => ({ data: [] })),
                materialsApi.getAll().catch(() => ({ data: [] })),
            ]);

            const projectsData = projectsRes.data || [];
            setProjects(projectsData);
            setStats({
                projects: projectsData.length,
                customers: customersRes.data?.length || 0,
                estimates: estimatesRes.data?.length || 0,
                materials: materialsRes.data?.length || 0,
            });
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ icon, title, value, color }: { icon: React.ReactNode, title: string, value: number, color: string }) => (
        <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 2 }}>
            <Box sx={{
                bgcolor: `${color}20`,
                color: color,
                p: 2,
                borderRadius: '50%',
                mr: 2,
                display: 'flex'
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {loading ? '...' : value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </Box>
        </Card>
    );

    return (
        <Box className="dashboard" sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <Box className="dashboard-header" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('maker.dashboard.title')}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    {t('maker.dashboard.welcome')}
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<Folder fontSize="large" />}
                        title={t('maker.dashboard.stats.projects')}
                        value={stats.projects}
                        color="#2196f3"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<MonetizationOn fontSize="large" />}
                        title={t('maker.dashboard.stats.estimates')}
                        value={stats.estimates}
                        color="#4caf50"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<People fontSize="large" />}
                        title={t('maker.dashboard.stats.customers')}
                        value={stats.customers}
                        color="#ff9800"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<Inventory fontSize="large" />}
                        title={t('maker.dashboard.stats.materials')}
                        value={stats.materials}
                        color="#9c27b0"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Add color="primary" /> {t('maker.dashboard.quick_actions')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<Folder />}
                                    onClick={() => navigate('/Maker/projects')}
                                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                                >
                                    {t('maker.projects.create')}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<Description />}
                                    onClick={() => navigate('/Maker/estimates')}
                                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                                >
                                    {t('maker.estimates.new')}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<Inventory />}
                                    onClick={() => navigate('/Maker/materials')}
                                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                                >
                                    {t('maker.materials.add')}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<PersonAdd />}
                                    onClick={() => navigate('/Maker/customers')}
                                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                                >
                                    {t('maker.customers.new')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History color="primary" /> {t('maker.dashboard.recent_activity')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {projects.length > 0 ? (
                            <List>
                                {projects.slice(0, 5).map((project) => (
                                    <ListItem key={project.id} divider disablePadding sx={{ py: 1 }}>
                                        <ListItemIcon>
                                            <Folder color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={project.name}
                                            secondary={`${t('maker.projects.meta.created')} ${new Date(project.createdDate).toLocaleDateString()}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                {t('maker.dashboard.no_activity')}
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Dashboard;
